import React from 'react';
import { Plan } from '../../types/PlanTypes';

interface PlanCardProps {
    plan: Plan;
    isCurrent: boolean;
    subscriptionStatus: string;
    planEndDate: string;
    isChangingPlan: boolean;
    hasActiveSubscription: boolean;
    onPlanChange: (plan: Plan) => void;
}

const getCurrentPlanButtonText = (
    isCurrent: boolean,
    subscriptionStatus: string,
    planName: string,
    hasActiveSubscription: boolean
) => {
    if (isCurrent) {
        if (subscriptionStatus === 'active') {
            return 'Current Plan';
        } else {
            return 'Re-Subscribe to ' + planName;
        }
    } else if (hasActiveSubscription) {
        return 'Switch to ' + planName;
    }
    return 'Subscribe to ' + planName;
};

const PlanCard: React.FC<PlanCardProps> = ({
    plan,
    isCurrent,
    subscriptionStatus,
    planEndDate,
    isChangingPlan,
    hasActiveSubscription,
    onPlanChange
}) => {
    const shouldDisableButton = (isCurrent && subscriptionStatus === 'active') || isChangingPlan || !plan.stripePriceId;

    return (
        <>
            <style>
                {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
            </style>
            <div
                style={{
                    border: isCurrent ? '2px solid #28a745' : '1px solid #ddd',
                    borderRadius: 8,
                    padding: 20,
                    backgroundColor: isCurrent ? '#f8fff8' : '#fff',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <div style={{ flex: 1 }}>
                    {isCurrent && (
                        <div style={{
                            position: 'absolute',
                            top: -1,
                            right: -1,
                            backgroundColor: '#28a745',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '0 8px 0 8px',
                            fontSize: 12,
                            fontWeight: 'bold'
                        }}>
                            CURRENT
                        </div>
                    )}

                    <h4 style={{ margin: '0 0 8px 0', color: '#333' }}>{plan.name}</h4>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#007bff', marginBottom: 8 }}>
                        {plan.priceString}
                    </div>
                    <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
                        {plan.description}
                    </p>

                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
                        {plan.features.map((feature, index) => (
                            <li key={index} style={{
                                marginBottom: 8,
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: 14
                            }}>
                                <span style={{ color: '#28a745', marginRight: 8 }}>✓</span>
                                {feature}
                            </li>
                        ))}
                    </ul>
                </div>

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!shouldDisableButton) {
                            onPlanChange(plan);
                        }
                    }}
                    disabled={shouldDisableButton}
                    style={{
                        background: isCurrent ? '#28a745' : !plan.stripePriceId ? '#ccc' : 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
                        color: 'white',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: 4,
                        fontSize: 14,
                        cursor: shouldDisableButton ? 'not-allowed' : 'pointer',
                        opacity: isChangingPlan ? 0.6 : 1,
                        fontWeight: 'bold',
                        marginTop: 'auto',
                        transition: 'all 0.2s ease',
                        transform: 'scale(1)',
                    }}
                    onMouseOver={(e) => {
                        if (!isCurrent && !isChangingPlan && plan.stripePriceId) {
                            e.currentTarget.style.transform = 'scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 123, 255, 0.3)';
                        }
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}
                    onMouseDown={(e) => {
                        if (!isCurrent && !isChangingPlan && plan.stripePriceId) {
                            e.currentTarget.style.transform = 'scale(0.98)';
                        }
                    }}
                    onMouseUp={(e) => {
                        if (!isCurrent && !isChangingPlan && plan.stripePriceId) {
                            e.currentTarget.style.transform = 'scale(1.02)';
                        }
                    }}
                >
                    {isChangingPlan ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid transparent',
                                borderTop: '2px solid white',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }} />
                            Processing...
                        </div>) :
                        getCurrentPlanButtonText(isCurrent, subscriptionStatus, plan.name, hasActiveSubscription)}
                </button>
            </div>
        </>
    );
};

export default PlanCard;
